import * as React from "react";
import { Box, Typography, Avatar, Grid, Divider, Chip } from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import { fetchUserDetail } from "../../DAL/fetch";
import LinearIndeterminate from "../../Components/LinearIndeterminate";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { baseUrl, fileUrl } from "../../Config/Config";

export default function UserDetail({ open, setOpen }) {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({});
  const { id } = useParams();
  const navigate = useNavigate();

  const FetchUserDetail = async () => {
    setLoading(true);
    try {
      const response = await fetchUserDetail(id);
      if (response && response.data) {
        setData(response.data.data || response.data);
      }
    } catch (error) {
      console.error(`Failed to fetch user details:`, error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    FetchUserDetail();
  }, [id]);

  const renderBoolean = (value) => {
    return value ? "Yes" : "No";
  };

  // Function to render fields
  const renderField = (key, value) => {
    if (Array.isArray(value)) {
      return (
        <Box key={key} sx={{ marginTop: 2 }}>
          <Typography variant="h6" sx={{ color: "#8F29D0" }}>
            <b>{key}:</b>
          </Typography>
          {value.map((item, index) => (
            <Box key={index} sx={{ marginLeft: 2 }}>
              {renderFields(item)}
            </Box>
          ))}
        </Box>
      );
    } else if (typeof value === "object" && value !== null) {
      return (
        <Box key={key} sx={{ marginTop: 2 }}>
          <Typography variant="h6" sx={{ color: "#8F29D0" }}>
            <b>{key}:</b>
          </Typography>
          <Box sx={{ marginLeft: 2 }}>{renderFields(value)}</Box>
        </Box>
      );
    } else if (typeof value === "boolean") {
      return (
        <Typography key={key} sx={{ marginTop: 1 }}>
          <strong>{key}:</strong> {renderBoolean(value)}
        </Typography>
      );
    } else {
      return (
        <Typography key={key} sx={{ marginTop: 1 }}>
          <strong>{key}:</strong> {value !== null ? value : "N/A"}
        </Typography>
      );
    }
  };

  const renderFields = (obj) => {
    return Object.entries(obj).map(([key, value]) => renderField(key, value));
  };

  return (
    <Box sx={{ padding: "3%" }}>
      {loading ? (
        <LinearIndeterminate />
      ) : (
        <>
          <Box
            sx={{
              display: "flex",
              marginBottom: 1,
              alignItems: "center",
              gap: "5px",
            }}
          >
            <ArrowBackIcon
              onClick={() => navigate(-1)}
              sx={{ cursor: "pointer" }}
            />
            <Typography
              variant="h6"
              gutterBottom
              sx={{ color: "#000", alignSelf: "center", mt: 1 }}
            >
              User Detail
            </Typography>
          </Box>

          <Box sx={{ marginBottom: 4 }}>
            {/* Avatar */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                marginBottom: 2,
              }}
            >
              <Avatar
                alt={data.name}
                src={
                  data.picture?.startsWith("http")
                    ? data.picture
                    : fileUrl + data.picture
                }
                sx={{ height: "100px", width: "100px" }}
              />
            </Box>

            {/* General Information */}
            <Typography
              variant="h6"
              gutterBottom
              sx={{ marginTop: 4, color: "#8F29D0" }}
            >
              <b>General Information</b>
              <Divider />
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={4}>
                <Typography>
                  <strong>Name:</strong> {data.name || "N/A"}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Typography>
                  <strong>Email:</strong> {data.email || "N/A"}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Typography>
                  <strong>Phone:</strong> {data.phone || "N/A"}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Typography>
                  <strong>Gender:</strong> {data.gender || "N/A"}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Typography>
                  <strong>Country:</strong> {data.country || "N/A"}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Typography>
                  <strong>Birth Day:</strong> {data.birthDay || "N/A"}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Typography>
                  <strong>Status:</strong>{" "}
                  {
                    <Chip
                      label={data.banned == false ? "UnBanned" : "Banned"}
                      color={data.banned == false ? "success" : "error"}
                      sx={{ height: "25px" }}
                    />
                  }
                </Typography>
              </Grid>
            </Grid>

            {/* Account Information */}
            <Typography
              variant="h6"
              gutterBottom
              sx={{ marginTop: 4, color: "#8F29D0" }}
            >
              <b>Account Information</b>
              <Divider />
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={4}>
                <Typography>
                  <strong>RID:</strong> {data.code || "N/A"}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Typography>
                  <strong>UUID:</strong> {data.id || "N/A"}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Typography>
                  <strong>Firebase UUID:</strong> {data.firebaseUid || "N/A"}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Typography
                  sx={{
                    overflow: "scroll",
                    scrollbarWidth: "none",
                  }}
                >
                  <strong>Push Notification Token:</strong>{" "}
                  {data.pushNotificationToken || "N/A"}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Typography>
                  <strong>Role:</strong> {data.role || "N/A"}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Typography>
                  <strong>Type:</strong> {data.type || "N/A"}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Typography>
                  <strong>Verified:</strong> {renderBoolean(data.verified)}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Typography>
                  <strong>Hosting Level:</strong>{" "}
                  {data.hostLevel == null
                    ? "N/A"
                    : data.hostLevel.level || "N/A"}
                </Typography>
              </Grid>
            </Grid>

            <Typography
              variant="h6"
              gutterBottom
              sx={{ marginTop: 4, color: "#8F29D0" }}
            >
              <b>Agency Information</b>
              <Divider />
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={4}>
                <Typography>
                  <strong>Agency ID:</strong> {data.agency?.code || "N/A"}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Typography>
                  <strong>Agency Name:</strong> {data.agency?.name || "N/A"}
                </Typography>
              </Grid>
            </Grid>
            <Typography
              variant="h6"
              gutterBottom
              sx={{ marginTop: 4, color: "#8F29D0" }}
            >
              <b>Product Information</b>
              <Divider />
            </Typography>
            <Grid container spacing={2}>
              {data.productMine && data.productMine.length > 0 ? (
                data.productMine.map((product) => (
                  <Grid item xs={12} sm={6} md={4} key={product.id}>
                    <Typography>
                      <strong>Product Name:</strong>{" "}
                      {product.product?.name || "N/A"} (
                      {product.isActive ? "Active" : "Inactive"})
                    </Typography>
                  </Grid>
                ))
              ) : (
                <Grid item xs={12}>
                  <Typography>N/A</Typography>
                </Grid>
              )}
            </Grid>

            {/* Social Statistics */}
            <Typography
              variant="h6"
              gutterBottom
              sx={{ marginTop: 4, color: "#8F29D0" }}
            >
              <b>Social Statistics</b>
              <Divider />
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={4}>
                <Typography>
                  <strong>Total Likes:</strong> {data.totalLikes || 0}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Typography>
                  <strong>Total Following:</strong> {data.totalFollowing || 0}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Typography>
                  <strong>Total Followers:</strong> {data.totalFollower || 0}
                </Typography>
              </Grid>
            </Grid>

            {/* Wallet Information */}
            <Typography
              variant="h6"
              gutterBottom
              sx={{ marginTop: 4, color: "#8F29D0" }}
            >
              <b>Wallet Information</b>
              <Divider />
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={4}>
                <Typography>
                  <strong>Wallet ID:</strong> {data.wallet?.id || "N/A"}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                <Typography>
                  <strong>Diamond:</strong> {data.wallet?.diamond || 0}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Typography>
                  <strong>Rcoin:</strong> {data.wallet?.rcoin || 0}
                </Typography>
              </Grid>
            </Grid>

            {/* System Level Information */}
            <Typography
              variant="h6"
              gutterBottom
              sx={{ marginTop: 4, color: "#8F29D0" }}
            >
              <b>Sending Level Information</b>
              <Divider />
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={4}>
                <Typography>
                  <strong>Level:</strong> {data?.systemLevel?.level || 0}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Typography>
                  <strong>Total Sending:</strong>{" "}
                  {data.systemLevel?.totalDiamond || 0}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Typography>
                  <strong>Current Level:</strong> {data?.systemLevel?.now || 0}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Typography>
                  <strong>Target Level:</strong>{" "}
                  {data?.systemLevel?.target || 0}
                </Typography>
              </Grid>
            </Grid>
          </Box>
        </>
      )}
    </Box>
  );
}
